/**
 * Plan presentation definitions and legacy compatibility helpers.
 * Billing/package/module entitlements live in billing.ts.
 */

import {
  ALL_KNOWN_MODULE_PATHS,
  type BillingPlanKey,
  computeTenantEnabledModules,
  filterModulesByOrgType as filterBillingModulesByOrgType,
  getResolvedEnabledModules,
  resolveEffectiveBillingPlan,
} from './billing';

export type PlanKey =
  | 'CI_FREE'
  | 'ESSENTIALS'
  | 'home_care_compliance'
  | 'PROFESSIONAL'
  | 'PROFESSIONAL_CLINICAL'
  | 'ENTERPRISE';

/** @deprecated Legacy plan key — use resolvePlanKey() from billing.ts */
export type LegacyPlanKey = 'STARTER';
export type OrgType = 'HOME_CARE' | 'RESIDENTIAL' | 'MIXED';

export const ORG_TYPES: { key: OrgType; name: string; description: string }[] = [
  { key: 'HOME_CARE', name: 'Home Care', description: 'In-home and community care services' },
  {
    key: 'RESIDENTIAL',
    name: 'Residential Aged Care',
    description: 'Facility-based aged care services',
  },
  { key: 'MIXED', name: 'Both', description: 'Combined residential and home care services' },
];

export interface PlanDefinition {
  key: PlanKey;
  name: string;
  tier: string;
  priceMonthly: number; // AUD
  maxFacilities: number; // -1 = unlimited
  maxUsers: number; // -1 = unlimited
  /** @deprecated Module entitlements are computed from billing.ts. Kept only for CI_FREE. */
  enabledModules?: string[] | null;
  features: string[];
  description: string;
}

// ─── Module Route Paths ─────────────────────────────────────
// Must match sidebar nav hrefs in dashboard-layout.tsx

const MY_WORKSPACE_MODULES = [
  '/me/profile',
  '/me/roster',
  '/me/shift-offers',
  '/me/shift-swaps',
  '/me/timesheets',
  '/me/messages',
  '/me/leave',
  '/me/training',
  '/me/supervision',
];

const CI_MODULES = [
  '/compliance/continuous-improvement',
  '/compliance/continuous-improvement?view=dashboard',
  '/compliance/continuous-improvement/new',
  '/compliance/continuous-improvement?view=actions',
  ...MY_WORKSPACE_MODULES,
];

/** Every known module path, generated from the canonical billing package registry. */
export const ALL_MODULE_PATHS = ALL_KNOWN_MODULE_PATHS;

// ─── Module Classification ──────────────────────────────────
// Essential modules cannot be disabled by tenant admins.

/**
 * Essential modules cannot be disabled by tenant admins within their tier.
 * Only includes modules that are critical infrastructure within each package —
 * higher-tier modules are gated by the tier itself, not this set.
 */
export const ESSENTIAL_MODULES = new Set([
  // Compliance Essentials (always-on within Essentials tier)
  '/clients',
  '/care-plans',
  '/compliance/incidents',
  '/compliance/screening',
  '/compliance/certifications',
  '/compliance/workforce',
  '/compliance/audit',
  '/compliance/audit-program',
  '/compliance/audit-trail',
  '/compliance/quality-indicators',
  // Workforce (always-on within Professional tier)
  '/staff',
  '/roster',
  // Clinical (always-on within Professional + Clinical tier)
  '/observations',
  '/medications',
  // Financial (always-on within Enterprise tier)
  '/finance/invoices',
  '/finance/claims',
  '/pricing/rate-cards',
  // Residential/Home Care (always-on within their org-type context)
  '/residential/rooms',
  '/residential/admissions',
  '/home-care/service-areas',
  '/home-care/schedules',
  '/home-care/classifications',
  // Monitoring (always-on within Clinical tier)
  '/monitoring/devices',
  '/monitoring/alerts',
  // Phase 3.4: /telehealth removed from ALWAYS_ON_MODULES — surface is
  // incomplete (no consent / recording / billing tables, P0/P1 PHI-leak
  // findings) and must be tenant-opt-in via tenant_configs.enabled_modules
  // until the controlled scheduler RPC is wired and a real video provider
  // is integrated. Re-enable here once Telehealth 2.0 ships.
  // Analytics (platform service, always on)
  '/analytics',
]);

// ─── Hard Dependency Rules ──────────────────────────────────
// Key depends on all values being enabled.

export const MODULE_DEPENDENCIES: Record<string, string[]> = {
  // Clinical → Care Management
  '/observations': ['/clients'],
  '/medications': ['/clients'],
  '/care-pathways': ['/clients', '/care-plans'],
  '/wounds': ['/clients'],
  '/advance-care-plans': ['/clients', '/care-plans'],
  // Residential → Care Management
  '/residential/rooms': ['/clients'],
  '/residential/admissions': ['/clients'],
  '/residential/occupancy': ['/residential/rooms'],
  '/residential/care-minutes': ['/residential/rooms'],
  '/residential/activities': ['/clients'],
  '/residential/visitors': ['/clients'],
  '/residential/community': ['/clients', '/residential/rooms'],
  '/residential/night-rounds': ['/residential/rooms'],
  '/residential/meal-management': ['/residential/rooms'],
  '/residential/daily-living': ['/residential/rooms', '/residential/meal-management'],
  // Home Care → Care Management + Workforce
  '/home-care/service-areas': ['/clients'],
  '/home-care/schedules': ['/clients', '/roster'],
  '/home-care/assessments': ['/clients'],
  // Pricing → Finance
  '/pricing/rate-cards': ['/finance/invoices'],
  '/pricing/agreements': ['/finance/invoices', '/pricing/rate-cards'],
  // Finance extended
  // SAH contributions are part of `sah_funding` and deliberately do not unlock
  // the full finance invoice suite for Home Care Essentials/Professional.
  '/finance/contributions': ['/clients'],
  '/finance/reports': ['/finance/invoices'],
  '/finance/accounting': ['/finance/invoices'],
  // Payroll route layout requires the parent module as well as child modules.
  '/payroll/readiness': ['/payroll'],
  '/payroll/manual-closures': ['/payroll'],
  '/payroll/provider-handoff': ['/payroll'],
  '/payroll/fixed-pay': ['/payroll'],
  '/payroll/corrections': ['/payroll'],
  '/payroll/policy': ['/payroll'],
  '/payroll/queue': ['/payroll'],
  '/payroll/pay-runs': ['/payroll'],
  '/payroll/timesheet': ['/payroll'],
  '/payroll/reconciliation': ['/payroll'],
  '/payroll/award-review': ['/payroll'],
  '/roster/import': ['/roster', '/roster/periods'],
  '/settings/integrations/xero/wizard': ['/settings/integrations/xero'],
  // Compliance extended
  '/compliance/standards': ['/compliance/incidents'],
  '/compliance/quality-indicators': ['/compliance/incidents'],
  '/compliance/clinical-governance': ['/compliance/incidents'],
  '/compliance/environmental': ['/compliance/incidents'],
  '/compliance/governance': ['/compliance/incidents'],
  '/compliance/complaints?tab=feedback': ['/compliance/standards'],
  '/compliance/quality-indicators?tab=submit': ['/compliance/quality-indicators?tab=data-entry'],
  '/compliance/quality/cip': ['/compliance/standards'],
  '/compliance/documents/expiring': ['/compliance/documents'],
  '/compliance/restrictive-practices': ['/compliance/incidents'],
  '/compliance/code-of-conduct': ['/compliance/incidents'],
  '/people/policies-conduct/code': ['/compliance/incidents'],
  '/compliance/complaints': ['/compliance/incidents'],
  '/compliance/whistleblower': ['/compliance/incidents'],
  '/compliance/prudential': ['/compliance/incidents'],
  '/compliance/data-breach': ['/compliance/incidents'],
  '/compliance/star-ratings': ['/compliance/incidents'],
  // Analytics extended
  '/analytics/reports': ['/analytics'],
  '/analytics/benchmarking': ['/analytics'],
  // Monitoring extended
  '/monitoring/vital-signs': ['/monitoring/devices'],
  '/monitoring/equipment': ['/monitoring/devices'],
  // Telehealth extended
  '/telehealth/schedule': ['/telehealth'],
  '/telehealth/recordings': ['/telehealth'],
};

// ─── Module Group Metadata ──────────────────────────────────
// Used by the Settings > Modules page to display grouped toggles.

export interface ModuleItem {
  href: string;
  label: string;
  essential: boolean;
}

export interface ModuleGroup {
  title: string;
  items: ModuleItem[];
}

/**
 * MODULE_GROUPS hrefs are provider-facing destinations. Purchased-module
 * entitlement keys in billing remain on the legacy paths below.
 */
const MODULE_ENTITLEMENT_BY_HREF: Record<string, string> = {
  '/people/workers': '/staff',
  '/people/learning': '/staff/training',
  '/people/compliance': '/compliance/workforce',
  '/people/compliance/screening': '/compliance/screening',
  '/people/compliance/certifications': '/compliance/certifications',
  '/people/policies-conduct/code': '/compliance/code-of-conduct',
};

/** Map a People/canonical module href to its purchased billing entitlement key. */
export function moduleEntitlementPath(href: string): string {
  return MODULE_ENTITLEMENT_BY_HREF[href] ?? href;
}

export const MODULE_GROUPS: ModuleGroup[] = [
  {
    title: 'CARE MANAGEMENT',
    items: [
      { href: '/clients', label: 'Clients', essential: true },
      { href: '/care-plans', label: 'Care plans', essential: true },
      { href: '/assessments', label: 'Assessments', essential: false },
      { href: '/programs', label: 'Programs', essential: false },
      { href: '/waitlist', label: 'Waitlist', essential: false },
    ],
  },
  {
    title: 'CLINICAL',
    items: [
      { href: '/observations', label: 'Observations', essential: true },
      { href: '/medications', label: 'Medications', essential: true },
      { href: '/care-pathways', label: 'Care pathways', essential: false },
      { href: '/wounds', label: 'Wounds', essential: false },
      { href: '/advance-care-plans', label: 'Advance care plans', essential: false },
    ],
  },
  {
    title: 'RESIDENTIAL',
    items: [
      { href: '/residential/rooms', label: 'Rooms & Beds', essential: true },
      { href: '/residential/admissions', label: 'Admissions', essential: true },
      { href: '/residential/occupancy', label: 'Occupancy', essential: false },
      { href: '/residential/care-minutes', label: 'Care Minutes', essential: false },
      { href: '/residential/activities', label: 'Activities', essential: false },
      { href: '/residential/visitors', label: 'Visitors', essential: false },
      { href: '/residential/community', label: 'Community', essential: false },
      { href: '/residential/night-rounds', label: 'Night Rounds', essential: false },
      { href: '/residential/meal-management', label: 'Meal Management', essential: false },
      { href: '/residential/daily-living', label: 'Daily Living', essential: false },
    ],
  },
  {
    title: 'HOME CARE',
    items: [
      { href: '/home-care/service-areas', label: 'Service Areas', essential: true },
      { href: '/home-care/schedules', label: 'Schedules', essential: true },
      { href: '/home-care/assessments', label: 'Home Assessments', essential: false },
      { href: '/home-care/service-delivery', label: 'Service Delivery', essential: false },
      {
        href: '/home-care/care-delivery-settings',
        label: 'Care delivery policies',
        essential: false,
      },
      { href: '/home-care/budgets', label: 'Budgets', essential: false },
      { href: '/home-care/funding-changes', label: 'Funding Changes', essential: false },
      { href: '/home-care/readiness', label: 'Onboarding Readiness', essential: false },
      { href: '/home-care/classifications', label: 'Classifications', essential: false },
      { href: '/home-care/claims', label: 'Claims', essential: false },
      { href: '/home-care/statements', label: 'Statements', essential: false },
      { href: '/home-care/exceptions', label: 'Exceptions', essential: false },
      { href: '/home-care/notes-review', label: 'Note Review', essential: false },
      { href: '/home-care/reconciliation', label: 'Reconciliation', essential: false },
      { href: '/home-care/at-hm', label: 'AT-HM', essential: false },
      { href: '/cm-tasks', label: 'CM time', essential: false },
      { href: '/reports/sah', label: 'SAH Reports', essential: false },
      { href: '/compliance/sah-evidence', label: 'SAH Evidence', essential: false },
    ],
  },
  {
    title: 'PRICING',
    items: [
      { href: '/pricing/rate-cards', label: 'Rate Cards', essential: true },
      { href: '/pricing/agreements', label: 'Agreements', essential: false },
    ],
  },
  {
    title: 'WORKFORCE',
    items: [
      { href: '/people/workers', label: 'Staff', essential: true },
      { href: '/roster', label: 'Roster', essential: true },
      { href: '/roster/timesheets', label: 'Timesheets', essential: false },
      { href: '/roster/time-review', label: 'Daily Time Review', essential: false },
      { href: '/roster/leave', label: 'Leave', essential: false },
      { href: '/people/compliance', label: 'Compliance', essential: false },
      { href: '/people/learning', label: 'Training', essential: false },
      { href: '/contractors', label: 'Contractors', essential: false },
      { href: '/roster/shift-swaps', label: 'Swaps', essential: false },
      { href: '/roster/handovers', label: 'Handovers', essential: false },
      // D9 (2026-08-25) retired /roster/forecast and /roster/optimiser: they are
      // in no package, so an admin toggling them on could never make them work.
      // A dead toggle is worse than an absent one - it implies a capability.
      { href: '/roster/health', label: 'Roster Health', essential: false },
      { href: '/roster/proposals', label: 'Proposals', essential: false },
      { href: '/roster/dispatch?view=day', label: 'Live Shifts Board', essential: false },
      { href: '/roster/wing-staffing', label: 'Wing Staffing', essential: false },
    ],
  },
  {
    title: 'FINANCE',
    items: [
      { href: '/finance/invoices', label: 'Invoices', essential: true },
      { href: '/finance/claims', label: 'Claims', essential: true },
      { href: '/finance/contributions', label: 'Contributions', essential: false },
      { href: '/finance/reports', label: 'Reports', essential: false },
      { href: '/finance/accounting', label: 'Accounting Sync', essential: false },
      { href: '/finance/an-acc', label: 'AN-ACC Funding', essential: false },
      { href: '/finance/residential-billing', label: 'Residential billing', essential: false },
      { href: '/finance/respite-billing', label: 'Respite billing', essential: false },
      { href: '/finance/accommodation', label: 'Accommodation Finance', essential: false },
      { href: '/finance/reports/statements', label: 'Client Statements', essential: false },
      { href: '/finance/reports/aged-debtors', label: 'Aged Debtors', essential: false },
      { href: '/finance/reports/reconciliation', label: 'Reconciliation', essential: false },
      { href: '/finance/budgets/carry-over', label: 'Budget Carry-Over', essential: false },
    ],
  },
  {
    title: 'SETTINGS & INTEGRATIONS',
    items: [
      { href: '/settings/organisation', label: 'Organisation', essential: false },
      { href: '/settings/system', label: 'System', essential: false },
      { href: '/settings/users', label: 'Users', essential: false },
      { href: '/settings/security', label: 'Security', essential: false },
      { href: '/settings/facilities', label: 'Facilities', essential: false },
      { href: '/settings/roster', label: 'Roster Settings', essential: false },
      { href: '/settings/payroll/eba', label: 'EA / Award Setup', essential: false },
      { href: '/settings/payroll/pay-roles', label: 'Pay Roles', essential: false },
      { href: '/settings/payroll/aba-sender', label: 'ABA Sender', essential: false },
      { href: '/settings/integrations/xero', label: 'Xero', essential: false },
      { href: '/settings/integrations/xero/backfill', label: 'Xero Backfill', essential: false },
      {
        href: '/settings/integrations/services-australia',
        label: 'Services Australia',
        essential: false,
      },
      { href: '/settings/import', label: 'Data Import', essential: false },
      { href: '/settings/modules', label: 'Module Settings', essential: false },
    ],
  },
  {
    title: 'MONITORING',
    items: [
      { href: '/monitoring/devices', label: 'IoT Devices', essential: true },
      { href: '/monitoring/alerts', label: 'Alerts', essential: true },
      { href: '/monitoring/vital-signs', label: 'Vital Signs', essential: false },
      { href: '/monitoring/equipment', label: 'AT-HM Equipment', essential: false },
    ],
  },
  {
    title: 'TELEHEALTH',
    items: [
      { href: '/telehealth', label: 'Sessions', essential: true },
      { href: '/telehealth/schedule', label: 'Schedule', essential: false },
      { href: '/telehealth/recordings', label: 'Recordings', essential: false },
    ],
  },
  {
    title: 'COMPLIANCE',
    items: [
      { href: '/compliance/incidents', label: 'Care incidents', essential: true },
      { href: '/people/compliance/screening', label: 'Worker screening', essential: true },
      {
        href: '/people/compliance/certifications',
        label: 'Worker certifications',
        essential: true,
      },
      { href: '/people/compliance', label: 'Workforce Compliance', essential: true },
      { href: '/compliance/audit-trail', label: 'Audit trail', essential: true },
      { href: '/compliance/audit-program', label: 'Audit Program', essential: true },
      { href: '/compliance/standards', label: 'Quality Hub', essential: false },
      { href: '/compliance/quality-indicators', label: 'Quality Standards', essential: false },
      {
        href: '/compliance/quality-indicators?tab=submit',
        label: 'AIHW QI Submit',
        essential: false,
      },
      { href: '/compliance/clinical-governance', label: 'Clinical Governance', essential: false },
      { href: '/compliance/environmental', label: 'Environmental', essential: false },
      { href: '/compliance/governance', label: 'Governance', essential: false },
      { href: '/compliance/complaints?tab=feedback', label: 'Consumer Feedback', essential: false },
      // /compliance/quality/cip redirects to continuous-improvement (SSOT).
      // Do not surface a second "legacy CIP" nav label — dual surfaces confuse operators.
      {
        href: '/compliance/continuous-improvement',
        label: 'Continuous improvement',
        essential: false,
      },
      {
        href: '/compliance/continuous-improvement?view=dashboard',
        label: 'Review',
        essential: false,
      },
      {
        href: '/compliance/continuous-improvement/new',
        label: 'New Improvement',
        essential: false,
      },
      {
        href: '/compliance/continuous-improvement?view=actions',
        label: 'Actions',
        essential: false,
      },
      { href: '/compliance/complaints', label: 'Complaints', essential: false },
      { href: '/compliance/whistleblower', label: 'Whistleblower', essential: false },
      {
        href: '/compliance/restrictive-practices',
        label: 'Restrictive Practices',
        essential: false,
      },
      { href: '/people/policies-conduct/code', label: 'Code of Conduct', essential: false },
      { href: '/compliance/prudential', label: 'Prudential Standards', essential: false },
      { href: '/compliance/data-breach', label: 'Data Breach (NDB)', essential: false },
      { href: '/compliance/star-ratings', label: 'Star Ratings', essential: false },
      {
        href: '/compliance/standards?tab=engagement',
        label: 'Stakeholder Engagement',
        essential: false,
      },
      { href: '/compliance/responsible-persons', label: 'Responsible Persons', essential: false },
      { href: '/compliance/ipc', label: 'Infection Prevention', essential: false },
      { href: '/compliance/whs', label: 'WHS', essential: false },
      { href: '/compliance/governance/risk-register', label: 'Risk Register', essential: false },
      { href: '/compliance/governance/conflicts', label: 'Conflict of Interest', essential: false },
      { href: '/compliance/documents', label: 'Compliance Documents', essential: false },
      { href: '/compliance/documents/expiring', label: 'Expiring Documents', essential: false },
      { href: '/compliance/registration', label: 'Registration', essential: false },
      { href: '/compliance/reporting', label: 'Compliance Reports', essential: false },
    ],
  },
  {
    title: 'ANALYTICS',
    items: [
      { href: '/analytics', label: 'Dashboard', essential: true },
      { href: '/reports', label: 'Reports', essential: false },
      { href: '/analytics/benchmarking', label: 'Benchmarking', essential: false },
    ],
  },
];

// ─── Plan Definitions ───────────────────────────────────────

export const PLAN_DEFINITIONS: Record<PlanKey, PlanDefinition> = {
  CI_FREE: {
    key: 'CI_FREE',
    name: 'Continuous Improvement (Free)',
    tier: 'ci-free',
    priceMonthly: 0,
    maxFacilities: 1,
    maxUsers: 5,
    enabledModules: CI_MODULES,
    description: 'Free PDSA-based continuous improvement register for aged care providers',
    features: [
      'PDSA improvement cycle management',
      'Root cause analysis (5 Whys, fishbone)',
      'Action tracking with assignees',
      'Evidence & document linking',
      'Board CI reports & Standard 2 evidence pack',
      'Effectiveness measurement & review scheduling',
      '1 facility, up to 5 users',
      '90-day free trial — no credit card required',
    ],
  },
  home_care_compliance: {
    key: 'home_care_compliance',
    name: 'Compliance Essentials (Home Care)',
    tier: 'essentials',
    priceMonthly: 12,
    maxFacilities: 3,
    maxUsers: -1,
    description: 'Home-care compliance registers and evidence, without SAH operations',
    features: [
      'Compliance registers and evidence',
      'Compliance-only people',
      'Up to 3 facilities, unlimited users',
    ],
  },
  ESSENTIALS: {
    key: 'ESSENTIALS',
    name: 'Essentials',
    tier: 'essentials',
    priceMonthly: 0, // Per-bed pricing managed in billing.ts
    maxFacilities: 3,
    maxUsers: -1,
    description:
      'Core compliance — 11 modules covering SIRS, Quality Standards, QI, Governance, and more',
    features: [
      'Resident Registry',
      'SIRS incident reporting & restrictive practices',
      'Quality Standards self-assessment',
      'Quality Indicators & benchmarks',
      'Governance, risk register & skills matrix',
      'Complaints, Code of Conduct, Whistleblower',
      'Responsible Persons register',
      'Continuous improvement',
      'Up to 3 facilities, unlimited users',
      'Self-serve onboarding',
    ],
  },
  PROFESSIONAL: {
    key: 'PROFESSIONAL',
    name: 'Professional',
    tier: 'professional',
    priceMonthly: 0,
    maxFacilities: 5,
    maxUsers: -1,
    description: 'Compliance + Workforce & Operations — rostering, care minutes, SCHADS',
    features: [
      'Everything in Essentials (11 modules)',
      'Workforce compliance & screening',
      'Rostering with SCHADS penalty rate calculator',
      'Care delivery & care minutes tracking',
      'WHS & safety',
      '24/7 RN coverage validation',
      'Up to 5 facilities, unlimited users',
      'Self-serve or guided onboarding',
    ],
  },
  PROFESSIONAL_CLINICAL: {
    key: 'PROFESSIONAL_CLINICAL',
    name: 'Professional + Clinical',
    tier: 'professional_clinical',
    priceMonthly: 0,
    maxFacilities: 10,
    maxUsers: -1,
    description:
      'Compliance + Workforce + Clinical Care — assessments, medications, IPC, telehealth',
    features: [
      'Everything in Professional (15 modules)',
      'Clinical assessments & vital signs',
      'Medications management & S8 register',
      'Wound management & care pathways',
      'Infection prevention & control',
      'Advance care planning',
      'Telehealth & remote monitoring',
      'Family & Carer Portals included',
      'Care Worker Mobile App included',
      'Guided onboarding required (included)',
      'Up to 10 facilities, unlimited users',
    ],
  },
  ENTERPRISE: {
    key: 'ENTERPRISE',
    name: 'Enterprise',
    tier: 'enterprise',
    priceMonthly: 0,
    maxFacilities: -1,
    maxUsers: -1,
    description: 'Full platform — compliance, clinical, workforce, financial, and Support at Home',
    features: [
      'Everything in Professional + Clinical (21 modules)',
      'Funding & claims (AN-ACC)',
      'Billing & finance',
      'Prudential compliance (RADs, liquidity)',
      'Accommodation management',
      'Support at Home (budgets, contributions, claims)',
      'AI predictive models & benchmarking',
      'Family & Carer Portals included',
      'Care Worker Mobile App included',
      'Managed onboarding included',
      'Dedicated account manager & custom SLA',
      'Unlimited facilities & users',
    ],
  },
};

export const PLAN_KEYS: PlanKey[] = [
  'CI_FREE',
  'ESSENTIALS',
  'home_care_compliance',
  'PROFESSIONAL',
  'PROFESSIONAL_CLINICAL',
  'ENTERPRISE',
];
export const PAID_PLAN_KEYS: PlanKey[] = [
  'ESSENTIALS',
  'home_care_compliance',
  'PROFESSIONAL',
  'PROFESSIONAL_CLINICAL',
  'ENTERPRISE',
];

/** Map legacy and billing plan keys to PlanKey */
export const LEGACY_PLAN_KEY_MAP: Record<string, PlanKey> = {
  STARTER: 'ESSENTIALS',
  // Billing plan keys (from billing.ts) → closest PlanKey equivalent
  residential_essentials: 'ESSENTIALS',
  residential_professional: 'PROFESSIONAL',
  residential_professional_clinical: 'PROFESSIONAL_CLINICAL',
  residential_enterprise: 'ENTERPRISE',
  // Home care plans map to their residential equivalents for module resolution
  home_care_essentials: 'ESSENTIALS',
  home_care_professional: 'PROFESSIONAL',
  home_care_complete: 'ENTERPRISE',
  // Legacy billing keys
  residential_starter: 'ESSENTIALS',
  home_care: 'ESSENTIALS',
  home_care_plus: 'PROFESSIONAL',
};

// ─── Org-Type-Aware Module Computation ──────────────────────

/** Resolve a potentially legacy plan key to the current PlanKey */
export function resolveLegacyPlanKey(key: string): PlanKey {
  // Tier consumers (including onboarding) use ESSENTIALS; billing resolvers
  // preserve the exact product key before entering this compatibility helper.
  if (key === 'home_care_compliance') return 'ESSENTIALS';
  if (key in PLAN_DEFINITIONS) return key as PlanKey;
  if (key in LEGACY_PLAN_KEY_MAP) return LEGACY_PLAN_KEY_MAP[key] ?? 'ESSENTIALS';
  return 'ESSENTIALS'; // Safe default
}

export function resolveBillingPlanForPlanKey(
  plan: PlanKey | string,
  orgType?: OrgType,
): BillingPlanKey | null {
  if (plan === 'CI_FREE') return null;

  if (plan === 'home_care_compliance') return 'home_care_compliance';
  const resolved = resolveLegacyPlanKey(plan);
  if (orgType === 'HOME_CARE') {
    if (resolved === 'ESSENTIALS') return 'home_care_essentials';
    if (resolved === 'PROFESSIONAL') return 'home_care_professional';
    if (resolved === 'PROFESSIONAL_CLINICAL' || resolved === 'ENTERPRISE') {
      return 'home_care_complete';
    }
  }

  return resolveEffectiveBillingPlan({ legacyPlan: plan });
}

/**
 * Derive org type from any plan key (PlanKey, billing plan key, or subscription_tier).
 * Home-care plans always map to HOME_CARE. Everything else defaults to RESIDENTIAL.
 * MIXED is never derived — it must be explicitly selected by the user and is only
 * valid for plans that span both care types (Enterprise / Complete).
 */
export function deriveOrgTypeFromPlan(planKey: string | null | undefined): OrgType {
  if (!planKey) return 'RESIDENTIAL';
  const lower = planKey.toLowerCase();
  if (lower.includes('home_care') || lower.startsWith('hc_')) return 'HOME_CARE';
  return 'RESIDENTIAL';
}

/**
 * Identity read for a stored organisation type.
 *
 * Missing or unrecognised values stay unresolved (`null`). This never invents
 * MIXED and never rewrites a stored MIXED against the billing plan. Module
 * entitlement still uses `normaliseOrgType`, which may collapse MIXED on
 * single-care plans (BUG-26).
 */
export function readStoredOrgType(explicit?: OrgType | string | null): OrgType | null {
  if (explicit == null) return null;
  const normalised = String(explicit).toUpperCase().trim();
  if (normalised === 'HOME_CARE') return 'HOME_CARE';
  if (normalised === 'RESIDENTIAL') return 'RESIDENTIAL';
  if (normalised === 'MIXED') return 'MIXED';
  return null;
}

/**
 * Identity read for a tenant row. Prefers the typed `tenants.org_type`
 * column (G-3, author-only until applied) and falls back to
 * `brand_config.orgType`. Unresolved when both are missing or unrecognised.
 */
export function readTenantOrgType(
  tenant:
    | {
        org_type?: string | null;
        brand_config?: Record<string, unknown> | null;
      }
    | null
    | undefined,
): OrgType | null {
  if (!tenant) return null;
  const fromColumn = readStoredOrgType(tenant.org_type);
  if (fromColumn) return fromColumn;
  const brand = tenant.brand_config;
  const fromJson = brand && typeof brand.orgType === 'string' ? brand.orgType : null;
  return readStoredOrgType(fromJson);
}

/**
 * Module-entitlement resolver — not the identity read.
 *
 * Rules (validated against the live-DB tenant snapshot on 2026-04-06):
 *  1. An explicit HOME_CARE or RESIDENTIAL is TRUSTED — a user/admin who set
 *     it knows their care type better than we can guess from a legacy tier
 *     map (e.g. "starter" → residential_essentials even though the tenant
 *     actually delivers home care).
 *  2. MIXED is only accepted on plans that span both care types (Enterprise /
 *     Complete). On any single-care-type plan, MIXED collapses to the
 *     plan-derived type — this is the BUG-26 fix (HC Essentials + MIXED
 *     was rendering residential modules).
 *  3. Missing or unrecognised values fall back to the plan-derived type.
 *  4. Case-insensitive: legacy rows stored "residential" (lowercase) and
 *     the TypeScript type uses "RESIDENTIAL" — we match either.
 *
 * Identity and payroll coverage must use `readStoredOrgType` /
 * `readTenantOrgType` so a stored MIXED is not silently rewritten and a
 * missing value is not invented as MIXED.
 */
export function normaliseOrgType(planKey: string, explicit?: OrgType | string | null): OrgType {
  if (planKey.toLowerCase() === 'home_care_compliance') return 'HOME_CARE';
  const derived = deriveOrgTypeFromPlan(planKey);
  const lower = String(planKey).toLowerCase();
  const supportsMixed = lower.includes('enterprise') || lower.includes('complete');

  const normalised = typeof explicit === 'string' ? explicit.toUpperCase().trim() : explicit;

  if (normalised === 'HOME_CARE') return 'HOME_CARE';
  if (normalised === 'RESIDENTIAL') return 'RESIDENTIAL';
  if (normalised === 'MIXED') return supportsMixed ? 'MIXED' : derived;
  return derived;
}

/** Get the plan's full module list from the canonical billing entitlement registry. */
export function getPlanModules(plan: PlanKey | string): string[] {
  if (plan === 'CI_FREE') return CI_MODULES;

  const billingPlan = resolveEffectiveBillingPlan({ legacyPlan: plan });
  const modules = getResolvedEnabledModules(billingPlan, []);
  return modules ?? ALL_KNOWN_MODULE_PATHS;
}

/** Filter a module list by org type — removes irrelevant modules */
function filterByOrgType(modules: string[], orgType: OrgType): string[] {
  return filterBillingModulesByOrgType(modules, orgType);
}

/** Compute the default enabledModules for a plan + org type combination */
export function getDefaultModulesForOrgType(plan: PlanKey, orgType: OrgType): string[] {
  if (plan === 'CI_FREE') return filterByOrgType(CI_MODULES, orgType);

  const billingPlan = resolveBillingPlanForPlanKey(plan, orgType);
  return filterByOrgType(
    getResolvedEnabledModules(billingPlan, []) ?? ALL_KNOWN_MODULE_PATHS,
    orgType,
  );
}

/** Compute final enabledModules from plan + org type - admin disabled modules */
export function computeEnabledModules(
  plan: PlanKey | string,
  orgType: OrgType,
  adminDisabledModules?: string[],
  systemEnabledModules?: string[],
): string[] {
  const billingPlan = resolveBillingPlanForPlanKey(plan, orgType);
  return (
    computeTenantEnabledModules({
      billingPlan,
      selectedPackages: [],
      orgType,
      systemEnabledModules,
      adminDisabledModules,
      legacyEnabledModules: plan === 'CI_FREE' ? CI_MODULES : null,
    }) ?? filterByOrgType(ALL_KNOWN_MODULE_PATHS, orgType)
  );
}

/** Check if a module can be disabled (not essential, not a required dependency of something enabled) */
export function canDisableModule(
  href: string,
  currentEnabled: string[],
): { allowed: boolean; reason?: string } {
  const entitlementHref = moduleEntitlementPath(href);
  if (ESSENTIAL_MODULES.has(entitlementHref) || ESSENTIAL_MODULES.has(href)) {
    return { allowed: false, reason: 'This module is required and cannot be disabled' };
  }
  // Check if any currently-enabled module depends on this one
  const dependents = Object.entries(MODULE_DEPENDENCIES)
    .filter(([, deps]) => deps.includes(entitlementHref) || deps.includes(href))
    .map(([mod]) => mod)
    .filter(
      (mod) => currentEnabled.includes(mod) || currentEnabled.includes(moduleEntitlementPath(mod)),
    );

  if (dependents.length > 0) {
    const labels = dependents.map((d) => {
      for (const g of MODULE_GROUPS) {
        const item = g.items.find((i) => i.href === d || moduleEntitlementPath(i.href) === d);
        if (item) return item.label;
      }
      return d;
    });
    return { allowed: false, reason: `Required by: ${labels.join(', ')}` };
  }
  return { allowed: true };
}

/** Get all modules that would need to be disabled if a given module is disabled (cascade) */
export function getCascadeDisables(href: string, currentEnabled: string[]): string[] {
  const toDisable: string[] = [];
  const queue = [href];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const [mod, deps] of Object.entries(MODULE_DEPENDENCIES)) {
      if (deps.includes(current) && currentEnabled.includes(mod) && !toDisable.includes(mod)) {
        toDisable.push(mod);
        queue.push(mod);
      }
    }
  }
  return toDisable;
}

/** Get all dependencies that would need to be enabled if a given module is enabled */
export function getCascadeEnables(href: string, currentEnabled: string[]): string[] {
  const toEnable: string[] = [];
  const deps = MODULE_DEPENDENCIES[href];
  if (!deps) return toEnable;
  for (const dep of deps) {
    if (!currentEnabled.includes(dep)) {
      toEnable.push(dep);
      toEnable.push(...getCascadeEnables(dep, [...currentEnabled, ...toEnable]));
    }
  }
  return [...new Set(toEnable)];
}

// ─── Go-Live Streamlined Defaults (2026-07-04) ──────────────
// New tenants start with the Tier-3 "power" surfaces gated OFF so early users
// see the operational core; SYSTEM_ADMIN (or Settings › Modules) re-enables
// per tenant as need arises. Prefixes here are org-agnostic and deliberately
// exclude anything on the regulatory floor (whistleblower, data-breach,
// casual-conversion, offboarding remain enabled — see
// reports/go-live-streamlining-review-2026-07-04.md §5).

export const STREAMLINED_DEFAULT_DISABLED_MODULE_PREFIXES: readonly string[] = [
  // Platform/system surfaces
  '/admin/system/ai-usage',
  '/admin/system/master-data',
  '/admin/system/scim',
  '/admin/system/sso',
  '/admin/system/tenant-config',
  '/admin/system/workflows',
  '/settings/data-management',
  '/settings/import',
  '/settings/webhooks',
  // Analytics depth (hub stays)
  '/analytics/benchmarking',
  '/analytics/board-pack',
  '/analytics/facilities',
  // Multi-entity
  '/head-office',
  '/regions',
  '/dashboards/executive',
  '/dashboards/multi-facility',
  '/dashboards/region-roll-up',
  '/dashboards/operations',
  '/dashboards/risk-correlation',
  '/dashboards/workforce-analytics',
  '/dashboards/iot',
  // Care-plan power tools
  '/care-pathways',
  '/care-plans/bulk-replan',
  '/care-plans/configuration',
  '/care-plans/synthesize',
  // Contractors / agency workforce
  '/contractors',
  '/me/contractor',
  '/payroll/agency-invoices',
  '/payroll/provider-handoff',
  // HR depth
  '/hr/clinical-supervision',
  '/hr/performance',
  '/me/performance',
  '/me/supervision',
  '/me/capability',
  '/compliance/capability-framework',
  // Roster power tools
  // '/roster/live' and '/roster/runtime' are deliberately NOT in this list:
  // both are pure redirect stubs into /roster (a core module), so disabling
  // them by default only produced "Access denied" for deep links to a board
  // the tenant could already see one page over.
  '/roster/forecast',
  '/roster/optimise-day',
  '/roster/optimiser',
  '/roster/recalls',
  '/roster/sleepover-disturbances',
  // D9 (2026-08-25) sold /roster/health, /roster/proposals and
  // /roster/wing-staffing in workforce_operations. Seeding them into a new
  // tenant's adminDisabledModules would strip the grant again and put a
  // packaged RAC provider straight back where this started: staffing demand,
  // roster health and the AN-ACC/RP proposals unreachable, with three dead
  // doors pointing at them. A surface that is sold must not be disabled by
  // default. forecast and optimiser stay listed - they are disposition RETIRE.
  // Reports depth
  '/reports/generations',
  '/reports/subscriptions',
  '/reports/workforce/recalls',
  // Compliance depth (registers/acknowledgments stay; investigation suites defer)
  '/compliance/code-of-conduct/allegations',
  '/compliance/code-of-conduct/analytics',
  '/compliance/code-of-conduct/investigations',
  '/compliance/code-of-conduct/reports',
  '/compliance/environmental',
  '/compliance/ndis-overlay',
  '/compliance/validity-rules',
  // Clinical depth
  '/clinical/pathways',
  '/clinical/restorative',
  '/clinical/supervision',
  '/clinical/delegations',
  // Telehealth + IoT monitoring
  '/telehealth',
  '/monitoring',
  // Misc
  '/waitlist',
  '/translate',
  // Government claiming integrations (not production until Q3 2026)
  '/settings/integrations/services-australia',
  '/settings/integrations/dhda',
  '/settings/integrations/hi',
  // EBA authoring is opt-in only (Award engines remain the default pay basis)
  '/settings/payroll/eba',
];

const CONTRACTOR_MODULE_PREFIXES = ['/contractors', '/me/contractor'] as const;

/** `/contractors` and `/me/contractor` and their sub-routes (Decision D4). */
export function isContractorModulePath(mod: string): boolean {
  return CONTRACTOR_MODULE_PREFIXES.some(
    (prefix) => mod === prefix || mod.startsWith(`${prefix}/`),
  );
}

/**
 * Compute the default adminDisabledModules seed for a NEW tenant. Expands the
 * streamlined prefixes against the tenant's would-be enabled set so the stored
 * list contains only effective entries, never disables an essential module,
 * and never strips a module that a remaining-enabled module depends on.
 */
export function computeDefaultAdminDisabledModules(options: {
  billingPlan: BillingPlanKey | null;
  selectedPackages?: unknown;
  orgType?: OrgType | null;
}): string[] {
  const enabled =
    computeTenantEnabledModules({
      billingPlan: options.billingPlan,
      selectedPackages: options.selectedPackages ?? [],
      orgType: options.orgType ?? null,
      adminDisabledModules: [],
    }) ?? [];

  const disabled = new Set(
    enabled.filter(
      (mod) =>
        !ESSENTIAL_MODULES.has(mod) &&
        // Home-care intake: waitlist→admission is operational core for SAH go-live.
        !(
          mod === '/waitlist' &&
          (options.orgType === 'HOME_CARE' || options.orgType === 'MIXED')
        ) &&
        // Decision D4 (Paul, 20 Oct 2026): contractor management is on by
        // default for NEW home-care and mixed tenants. Residential is
        // unchanged and existing tenants are never re-seeded.
        !(
          isContractorModulePath(mod) &&
          (options.orgType === 'HOME_CARE' || options.orgType === 'MIXED')
        ) &&
        STREAMLINED_DEFAULT_DISABLED_MODULE_PREFIXES.some(
          (prefix) => mod === prefix || mod.startsWith(`${prefix}/`),
        ),
    ),
  );

  // Dependency safety: keep any module that a remaining-enabled module requires.
  let changed = true;
  while (changed) {
    changed = false;
    for (const mod of enabled) {
      if (disabled.has(mod)) continue;
      for (const dep of MODULE_DEPENDENCIES[mod] ?? []) {
        if (disabled.delete(dep)) changed = true;
      }
    }
  }

  return Array.from(disabled).sort();
}

/** organisations.plan_tier is a legacy bootstrap column; tenants is canonical. */
export function organisationPlanTier(
  tier: string,
): 'trial' | 'starter' | 'professional' | 'enterprise' {
  if (tier === 'ci-free' || tier === 'trial') return 'trial';
  if (tier === 'enterprise') return 'enterprise';
  if (tier === 'professional' || tier === 'professional_clinical') return 'professional';
  return 'starter';
}
